import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  getDoc,
  orderBy,
  documentId,
} from "firebase/firestore";
import db from "../../config/firebase";
import { SockProduct } from "../../models/sock-product";
import { SpecialsData } from "../../models/specials";

export const fetchAllProducts = createAsyncThunk(
  "products/fetchAllProducts",
  async () => {
    const productsCollection = collection(db, "products");
    const q = query(productsCollection, orderBy("subcategory", "asc"));
    const snapshot = await getDocs(q);

    return snapshot.docs.map(
      (doc) =>
        ({
          id: doc.id,
          ...doc.data(),
          createdAt: doc.data().createdAt.toDate(),
          updatedAt: doc.data().updatedAt.toDate(),
        } as SockProduct)
    );
  }
);
export const fetchProductById = createAsyncThunk(
  "products/fetchProductById",
  async (productId: string, { getState }) => {
    const state: any = getState();

    // Check if product already exists in current items
    const existingProduct = state.products.items.find(
      (p: SockProduct) => p.id === productId
    );
    if (existingProduct) {
      return existingProduct;
    }

    // Check if product exists in currentProduct cache
    if (state.products.currentProduct?.id === productId) {
      return state.products.currentProduct;
    }

    const productDoc = doc(db, "products", productId);
    const productSnapshot = await getDoc(productDoc);

    if (!productSnapshot.exists()) {
      throw new Error("Product not found");
    }

    return {
      id: productSnapshot.id,
      ...productSnapshot.data(),
      createdAt: productSnapshot.data().createdAt.toDate(),
      updatedAt: productSnapshot.data().updatedAt.toDate(),
    } as SockProduct;
  }
);

export const fetchByCategory = createAsyncThunk(
  "products/fetchByCategory",
  async (categoryId: string, { getState }) => {
    const state: any = getState();
    if (state.products.lastFetchedCategory === categoryId) {
      return { items: state.products.items, category: categoryId };
    }

    const productsCollection = collection(db, "products");
    const q = query(productsCollection, where("category", "==", categoryId));
    const snapshot = await getDocs(q);
    const items = snapshot.docs.map(
      (doc) =>
        ({
          id: doc.id,
          ...doc.data(),
          createdAt: doc.data().createdAt.toDate(),
          updatedAt: doc.data().updatedAt.toDate(),
        } as SockProduct)
    );

    return { items, category: categoryId };
  }
);

export const fetchBySubcategory = createAsyncThunk(
  "products/fetchBySubcategory",
  async (subcategoryId: string, { getState }) => {
    const state: any = getState();
    if (state.products.lastFetchedSubcategory === subcategoryId) {
      return { items: state.products.items, subcategory: subcategoryId };
    }

    const productsCollection = collection(db, "products");
    const q = query(
      productsCollection,
      where("subcategory", "==", subcategoryId)
    );
    const snapshot = await getDocs(q);
    const items = snapshot.docs.map(
      (doc) =>
        ({
          id: doc.id,
          ...doc.data(),
          createdAt: doc.data().createdAt.toDate(),
          updatedAt: doc.data().updatedAt.toDate(),
        } as SockProduct)
    );

    return { items, subcategory: subcategoryId };
  }
);

const searchCache = new Map<string, SockProduct[]>();

/// Specials
export const fetchSpecialProducts = createAsyncThunk(
  "products/fetchSpecialProducts",
  async () => {
    const specialsDoc = doc(db, "admin/specials");
    const specialsSnapshot = await getDoc(specialsDoc);

    if (!specialsSnapshot.exists()) {
      return { items: [], label: null, enabled: false, products: [] };
    }

    const specialsData = specialsSnapshot.data() as SpecialsData;

    if (
      !specialsData.enabled ||
      !specialsData.label ||
      !specialsData.products?.length
    ) {
      return {
        items: [],
        label: null,
        enabled: specialsData.enabled,
        products: specialsData.products || [],
      };
    }


    // 1. Get the array of product IDs for fetch.
    const productIdsToFetch = specialsData.products;

    // 2. Create a single query to get all documents where the ID is in your array.
    // Note: An 'in' query is limited to 30 items per query, for 4, this is perfect.
    const productsCollection = collection(db, "products");
    const q = query(productsCollection, where(documentId(), "in", productIdsToFetch));

    // 3. Execute the single query to get all products at once.
    const querySnapshot = await getDocs(q);

    const items: SockProduct[] = [];
    querySnapshot.forEach((doc) => {
      // Reconstruct each product object
      const data = doc.data();
      items.push({
        id: doc.id,
        ...data,
        createdAt: data.createdAt.toDate(),
        updatedAt: data.updatedAt.toDate(),
      } as SockProduct);
    });


    return {
      items, // The items fetched from the single query
      label: specialsData.label,
      enabled: specialsData.enabled,
      products: specialsData.products,
    };
  }
);
/// Search
export const searchProducts = createAsyncThunk(
  "products/searchProducts",
  async (searchTerm: string) => {
    // Check if we have cached results
    if (searchCache.has(searchTerm)) {
      return {
        items: searchCache.get(searchTerm),
        searchTerm,
      };
    }

    const productsCollection = collection(db, "products");
    const snapshot = await getDocs(productsCollection);

    // Perform client-side search
    const items = snapshot.docs
      .map(
        (doc) =>
          ({
            id: doc.id,
            ...doc.data(),
            createdAt: doc.data().createdAt.toDate(),
            updatedAt: doc.data().updatedAt.toDate(),
          } as SockProduct)
      )
      .filter(
        (product) =>
          product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          product.description.toLowerCase().includes(searchTerm.toLowerCase())
      );

    // Cache the results
    searchCache.set(searchTerm, items);

    return { items, searchTerm };
  }
);

const productSlice = createSlice({
  name: "products",
  initialState: {
    items: [] as SockProduct[],
    currentProduct: null as SockProduct | null,
    loading: false,
    specialProductsLoading: false,
    currentProductLoading: false,
    error: null as string | null,
    currentProductError: null as string | null,
    lastFetchedCategory: null as string | null,
    lastFetchedSubcategory: null as string | null,
    lastSearchTerm: null as string | null,
    specialProducts: {
      items: [] as SockProduct[],
      label: null as string | null,
      enabled: false,
      products: [] as string[],
    },
  },
  reducers: {
    clearCurrentProduct: (state) => {
      state.currentProduct = null;
      state.currentProductError = null;
    },
    clearError: (state) => {
      state.error = null;
      state.currentProductError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch single product by ID
      .addCase(fetchProductById.pending, (state) => {
        state.currentProductLoading = true;
        state.currentProductError = null;
      })
      .addCase(fetchProductById.fulfilled, (state, action) => {
        state.currentProduct = action.payload;
        state.currentProductLoading = false;
        state.currentProductError = null;
      })
      .addCase(fetchProductById.rejected, (state, action) => {
        state.currentProductLoading = false;
        state.currentProductError =
          action.error.message || "Failed to fetch product";
        state.currentProduct = null;
      })

      .addCase(fetchSpecialProducts.pending, (state) => {
          state.specialProductsLoading = true;
      })
      .addCase(fetchSpecialProducts.fulfilled, (state, action) => {
        state.specialProducts = action.payload;
        state.specialProductsLoading = false;
        state.error = null;
      })
      .addCase(fetchSpecialProducts.rejected, (state, action) => {
      state.specialProductsLoading = false;
        state.error =
          action.error.message || "Failed to fetch special products";
      })
      .addCase(fetchAllProducts.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchAllProducts.fulfilled, (state, action) => {
        state.items = action.payload;
        state.lastFetchedCategory = null;
        state.lastFetchedSubcategory = null;
        state.loading = false;
        state.error = null;
      })
      .addCase(fetchAllProducts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to fetch all products";
      })
      .addCase(fetchByCategory.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchByCategory.fulfilled, (state, action) => {
        if (state.lastFetchedCategory !== action.payload.category) {
          state.items = action.payload.items;
        }
        state.lastFetchedCategory = action.payload.category;
        state.lastFetchedSubcategory = null; /// Reset subcategory when switching categories
        state.loading = false;
        state.error = null;
      })
      .addCase(fetchByCategory.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.error.message || "Failed to fetch products by category";
      })
      .addCase(fetchBySubcategory.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchBySubcategory.fulfilled, (state, action) => {
        if (state.lastFetchedSubcategory !== action.payload.subcategory) {
          state.items = action.payload.items;
        }
        state.lastFetchedSubcategory = action.payload.subcategory;
        state.loading = false;
        state.error = null;
      })
      .addCase(fetchBySubcategory.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.error.message || "Failed to fetch products by subcategory";
      })

      // Search reducers
      .addCase(searchProducts.pending, (state) => {
        state.loading = true;
      })
      .addCase(searchProducts.fulfilled, (state, action) => {
        state.items = action.payload.items || [];
        state.lastSearchTerm = action.payload.searchTerm;
        state.lastFetchedCategory = null;
        state.lastFetchedSubcategory = null;
        state.loading = false;
        state.error = null;
      })
      .addCase(searchProducts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to search products";
      });
  },
});

export const { clearCurrentProduct, clearError } = productSlice.actions;
export default productSlice.reducer;
